import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { trackEvent } from './MetaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterForm = ({ variant = 'default' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Por favor, introduce un email válido');
      return;
    }

    setStatus('loading');
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage('¡Gracias por suscribirte! Te mantendremos informado.');
        trackEvent('Lead', { content_name: 'newsletter_subscription' });
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Error al suscribirse. Inténtalo de nuevo.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Error de conexión. Inténtalo más tarde.');
    }
  };

  const isCompact = variant === 'compact';

  return (
    <div 
      className={`${isCompact ? '' : 'bg-[#0d1620] border border-[#50c878]/20 rounded-xl p-4 sm:p-6'}`}
      data-testid="newsletter-form"
    >
      {!isCompact && (
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#50c878]/20 flex items-center justify-center">
            <Mail className="w-5 h-5 text-[#50c878]" />
          </div>
          <div>
            <h3 className="text-[#f5f5dc] font-semibold">Newsletter</h3>
            <p className="text-[#f5f5dc]/50 text-sm">Recibe novedades sobre la película</p>
          </div>
        </div>
      )}

      {status === 'success' ? (
        <div className="flex items-center gap-3 py-4 text-green-400">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{message}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className={`flex ${isCompact ? 'flex-row' : 'flex-col sm:flex-row'} gap-2`}>
            <Input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === 'error') setStatus('idle');
              }}
              placeholder="tu@email.com"
              className={`flex-1 bg-[#1a2b3c] border-[#50c878]/30 text-[#f5f5dc] placeholder:text-[#f5f5dc]/40 focus:border-[#50c878] ${
                isCompact ? 'py-2' : 'py-2.5'
              }`}
              data-testid="newsletter-email-input"
              disabled={status === 'loading'}
            />
            <Button
              type="submit"
              disabled={status === 'loading'}
              className={`bg-[#50c878] text-white hover:bg-[#40a0a0] ${
                isCompact ? 'px-4 py-2' : 'px-6 py-2.5'
              } rounded-lg font-medium whitespace-nowrap`}
              data-testid="newsletter-submit"
            >
              {status === 'loading' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                isCompact ? 'Suscribirse' : 'Suscribirme'
              )}
            </Button>
          </div>
          
          {status === 'error' && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{message}</span>
            </div>
          )}
          
          {!isCompact && (
            <p className="text-[#f5f5dc]/40 text-xs">
              Al suscribirte aceptas nuestra política de privacidad. No enviamos spam.
            </p>
          )}
        </form>
      )}
    </div>
  );
};

export default NewsletterForm;
